import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, GraduationCap, Swords, BookOpen, FlaskConical,
  Scale, Briefcase, Award, Target, TrendingUp, Landmark,
  Brain, Calculator, Shield, Palette, Stethoscope, LayoutDashboard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_META = {
  engineering: { icon: Calculator, color: 'from-blue-500 to-cyan-500', label: 'Engineering' },
  medical: { icon: Stethoscope, color: 'from-emerald-500 to-teal-500', label: 'Medical' },
  law: { icon: Scale, color: 'from-amber-500 to-orange-500', label: 'Law' },
  civil_services: { icon: Landmark, color: 'from-violet-500 to-purple-500', label: 'Civil Services' },
  defense: { icon: Shield, color: 'from-red-500 to-rose-500', label: 'Defense' },
  banking: { icon: Briefcase, color: 'from-green-500 to-emerald-500', label: 'Banking & Finance' },
  management: { icon: TrendingUp, color: 'from-pink-500 to-rose-500', label: 'Management' },
  school: { icon: Brain, color: 'from-indigo-500 to-blue-500', label: 'School Level' },
  teaching: { icon: GraduationCap, color: 'from-teal-500 to-cyan-500', label: 'Teaching' },
  design: { icon: Palette, color: 'from-fuchsia-500 to-pink-500', label: 'Design' },
  science: { icon: FlaskConical, color: 'from-yellow-500 to-amber-500', label: 'Science' },
  agriculture: { icon: Target, color: 'from-lime-500 to-green-500', label: 'Agriculture' },
  government: { icon: Landmark, color: 'from-sky-500 to-blue-500', label: 'Government' },
  board_exam: { icon: BookOpen, color: 'from-orange-500 to-red-500', label: 'Board Exams' },
  general: { icon: Award, color: 'from-slate-500 to-gray-500', label: 'General' },
};

export default function StudyMaterialHub() {
  const { token } = useAuth();
  const [exams, setExams] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/study-material/exams`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setExams(d.exams || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const categories = ['all', ...new Set(exams.map(e => e.category))];

  const filtered = exams.filter(e => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'all' || e.category === activeCategory;
    return matchSearch && matchCat;
  });

  const boardExams = filtered.filter(e => e.type === 'board');
  const competitiveExams = filtered.filter(e => e.type === 'competitive');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-material-hub">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/8 via-transparent to-cyan-500/8" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Study Material</h1>
              <p className="text-sm text-muted-foreground">Creative, detailed notes for every exam & topic</p>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" data-testid="go-dashboard-btn">
                <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </Link>
          </div>

          {/* Search */}
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search exams..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-card border-border"
              data-testid="exam-search"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map(cat => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.general;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeCategory === cat
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  data-testid={`cat-${cat}`}
                >
                  {cat === 'all' ? 'All Exams' : meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 space-y-10">
        {/* Maharashtra SSC Board */}
        {(() => {
          const sscExams = filtered.filter(e => e.type === 'board' && e.exam_id.startsWith('board_ssc'));
          return sscExams.length > 0 ? (
            <ExamSection title="Maharashtra SSC Board (Class 5-10)" icon={GraduationCap} exams={sscExams} />
          ) : null;
        })()}

        {/* Maharashtra HSC Board */}
        {(() => {
          const hscExams = filtered.filter(e => e.type === 'board' && e.exam_id.startsWith('board_hsc'));
          return hscExams.length > 0 ? (
            <ExamSection title="Maharashtra HSC Board (Class 11-12)" icon={GraduationCap} exams={hscExams} />
          ) : null;
        })()}

        {/* CBSE Board */}
        {(() => {
          const cbseExams = filtered.filter(e => e.type === 'board' && e.exam_id.startsWith('board_cbse'));
          return cbseExams.length > 0 ? (
            <ExamSection title="CBSE Board" icon={BookOpen} exams={cbseExams} />
          ) : null;
        })()}

        {/* Competitive Exams */}
        {competitiveExams.length > 0 && (
          <ExamSection title="Competitive Exams" icon={Swords} exams={competitiveExams} />
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No exams match your search</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ExamSection({ title, icon: Icon, exams }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <Icon className="w-5 h-5 text-primary" />{title}
        <Badge variant="outline" className="text-xs ml-2">{exams.length}</Badge>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.map(exam => (
          <ExamCard key={exam.exam_id} exam={exam} />
        ))}
      </div>
    </div>
  );
}

function ExamCard({ exam }) {
  const meta = CATEGORY_META[exam.category] || CATEGORY_META.general;
  const Icon = meta.icon;

  return (
    <Link
      to={`/study-material/${exam.exam_id}`}
      className="group relative rounded-2xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
      data-testid={`exam-card-${exam.exam_id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{exam.name}</h3>
          {exam.full_name !== exam.name && (
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{exam.full_name}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-2">
            {exam.subjects.slice(0, 3).map(s => (
              <Badge key={s} variant="outline" className="text-[9px] px-1.5 py-0 border-border/60">{s}</Badge>
            ))}
            {exam.subjects.length > 3 && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-border/60">
                +{exam.subjects.length - 3}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
        <Badge className={`text-[10px] ${exam.difficulty === 'Hard' ? 'bg-red-500/10 text-red-400' : exam.difficulty === 'Easy' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
          {exam.difficulty}
        </Badge>
        <span className="text-[10px] text-muted-foreground">{exam.subjects.length} subjects</span>
      </div>
    </Link>
  );
}
